import React from 'react';
import {
  Badge, Box, Button, Heading, HStack, Spacer, Text, useToast,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import GlassCard from './GlassCard';
import LedgerTable, { LedgerColumn } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  useInvoiceEmailDelivery, useRetryInvoiceEmail, InvoiceEmailRow,
  EMAIL_ABANDONED, EMAIL_PENDING,
} from '../hooks/useInvoiceEmailDelivery';

// item 1329 (Chad: "add a verification they sent and show a failure to send nag") — DELIVERY
// for subscription invoice emails.
//
// Item 1326 turned Stripe's own invoice emails off, which made Basix the only sender. Before
// that, a Basix send that failed was invisible but harmless — Stripe still delivered a copy.
// Now a failed send is a customer holding no invoice, and the old code recorded it as a log
// warning nobody reads. 16 of the 25 live subscriptions bill on the 1st, so this arrives as a
// burst or not at all.
//
// The card is deliberately QUIET when everything is fine: a one-line green confirmation with
// the delivered count, because "it worked" is exactly what you want to see on the 1st and it
// should cost no screen space. It only becomes a nag when something actually failed.
// Admin/accountant only (it exposes customer addresses and can re-send).
const InvoiceEmailDeliverySection: React.FC = () => {
  const { isAdmin } = useCurrentUser();
  const { data } = useInvoiceEmailDelivery(isAdmin);
  const retry = useRetryInvoiceEmail();
  const toast = useToast();

  if (!isAdmin || !data) return null;

  const failed = data.rows.filter((r) => r.status === EMAIL_ABANDONED);
  const pending = data.rows.filter((r) => r.status === EMAIL_PENDING);
  const nothingToReport = failed.length === 0 && pending.length === 0;

  // nothing wrong and nothing sent yet — say nothing at all
  if (nothingToReport && data.sent === 0) return null;

  const onRetry = async (row: InvoiceEmailRow) => {
    try {
      await retry.mutateAsync(row.id);
      toast({
        status: 'success',
        title: 'Queued for retry',
        description: `${row.recipient} will be attempted again within a couple of minutes.`,
      });
    } catch {
      toast({ status: 'error', title: 'Could not queue that retry', description: 'Try again.' });
    }
  };

  // the all-clear: one line, no table
  if (nothingToReport) {
    return (
      <GlassCard p={3}>
        <HStack spacing={2}>
          <Box color="green.400"><FiCheckCircle /></Box>
          <Text fontSize="sm">
            {`All ${data.sent} subscription invoice email${data.sent === 1 ? '' : 's'} delivered`}
          </Text>
          <Text fontSize="xs" opacity={0.6}>(last 60 days)</Text>
        </HStack>
      </GlassCard>
    );
  }

  const columns: LedgerColumn<InvoiceEmailRow>[] = [
    {
      // item 1445 (Chad: "a pass on all the ledgers … the name at the bottom under the other items like the portal") —
      // INVOICE = number bold, subscription name as subtext, customer beneath (CUSTOMER column folded in).
      // item 1447 — subscription + customer ride the full-width rowSubline; number here.
      key: 'invoiceNumber',
      header: 'INVOICE',
      width: '150px',
      render: (r) => <Text fontSize="sm" fontWeight="600" whiteSpace="nowrap">{r.invoiceNumber ?? '—'}</Text>,
    },
    { key: 'recipient', header: 'SENT TO', render: (r) => <Text fontSize="sm">{r.recipient}</Text> },
    {
      key: 'status',
      header: 'STATUS',
      render: (r) => (r.status === EMAIL_ABANDONED ? (
        <Badge colorScheme="red">{`GAVE UP after ${r.attempts}`}</Badge>
      ) : (
        <Badge colorScheme="orange">{`RETRYING (${r.attempts})`}</Badge>
      )),
    },
    {
      key: 'lastError',
      header: 'WHY',
      render: (r) => (
        <Text fontSize="xs" opacity={0.8} maxW="320px" noOfLines={2}>{r.lastError ?? '—'}</Text>
      ),
    },
  ];

  return (
    <GlassCard p={4}>
      <HStack mb={2} spacing={2}>
        <Box color={failed.length > 0 ? 'red.400' : 'orange.300'}><FiAlertTriangle /></Box>
        <Heading size="sm">INVOICE EMAIL DELIVERY</Heading>
        {failed.length > 0 && (
          <Badge colorScheme="red">{`${failed.length} NOT DELIVERED`}</Badge>
        )}
        {pending.length > 0 && (
          <Badge colorScheme="orange">{`${pending.length} retrying`}</Badge>
        )}
        <Spacer />
        <Text fontSize="xs" opacity={0.6}>{`${data.sent} delivered`}</Text>
      </HStack>
      <Text fontSize="xs" opacity={0.75} mb={3}>
        {failed.length > 0
          ? 'These customers have NOT received their invoice. Basix is the only sender, so nobody '
            + 'else emailed them either. Fix the address on the contact record, then Retry.'
          : 'These are being retried automatically — 2 minutes, then 5, 15, an hour, 3 hours.'}
      </Text>
      <LedgerTable
        columns={columns}
        rowSubline={(r) => <LedgerSubline name={r.subscriptionName} meta={[r.customerName]} />}
        rows={[...failed, ...pending]}
        rowId={(r) => r.id}
        searchText={(r) => `${r.customerName ?? ''} ${r.recipient} ${r.invoiceNumber ?? ''}`}
        emptyText="Every subscription invoice email has been delivered."
        rowActions={(r) => (r.status === EMAIL_ABANDONED ? (
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<FiRefreshCw />}
            isLoading={retry.isLoading}
            onClick={() => onRetry(r)}
          >
            Retry
          </Button>
        ) : null)}
      />
    </GlassCard>
  );
};

export default InvoiceEmailDeliverySection;
